import {
    Avatar,
    Box,
    Button,
    Center,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Table,
    Tabs,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast
} from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { MdAdd } from "react-icons/md";
import { useParams } from "react-router-dom";
import { Loader } from "../../components/Loader";
import { Title } from "../../components/Title";
import {
    AccountProfileImageForm,
    AccountProfileLanguageForm,
    ChangePasswordForm,
    IAccountProfileImageFormValues,
    IAccountProfileLanguageFormValues,
    IChangePasswordFormValues,
    IDoctorUpdateFormValues,
    ISpecialistUpdateFormValues,
    ISystemUserUpdateFormValues,
    UpdateDoctorForm,
    UpdateSpecialistForm,
    UpdateSystemUserForm
} from "../../forms";
import {
    AccountRole,
    AccountType,
    ClientProfile,
    DoctorProfile,
    MeDocument,
    SystemUserProfile,
    useAccountQuery,
    useChangePasswordMutation,
    useMeQuery,
    useSetAccountProfilePictureMutation,
    useUpdateDoctorMutation,
    useUpdateLanguageMutation,
    useUpdateSpecialistMutation,
    useUpdateSystemUserMutation
} from "../../graphql";
import { useAuthToken } from "../../hooks/useAuthToken";

const DoctorTable = ({ account }) => {
    const { t } = useTranslation();

    return (
        <Table variant="simple">
            <Thead>
                <Tr>
                    <Th isNumeric width="40%"></Th>
                    <Th width="50%"></Th>
                </Tr>
            </Thead>
            <Tbody>
                <Tr>
                    <Td isNumeric>{ t("login.email") }</Td>
                    <Td>{ account.email }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.mobileNumber") }</Td>
                    <Td>{ account.phoneNumber }</Td>
                </Tr>

                <Tr>
                    <Td isNumeric>{ t("login.birthday") }</Td>
                    <Td>{ (account.profile as DoctorProfile).birthday }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.sex") }</Td>
                    <Td>{ (account.profile as DoctorProfile).sex }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.language") }</Td>
                    <Td>{ account.language.name }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.firstRegistration") }</Td>
                    <Td>{ account.createdAt }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.accountStatus") }</Td>
                    <Td>{ account.accountStatus }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.country") }</Td>
                    <Td>{ (account.profile as DoctorProfile).country.name }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.city") }</Td>
                    <Td>{ (account.profile as DoctorProfile).city.name }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.healthCenter") }</Td>
                    <Td>{ (account.profile as DoctorProfile).healthCenter.name }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.licenceNumber") }</Td>
                    <Td>{ (account.profile as DoctorProfile).licenceNumber }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.speciality") }</Td>
                    <Td>{ (account.profile as DoctorProfile).speciality?.name }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.bio") }</Td>
                    <Td>{ (account.profile as DoctorProfile).bio }</Td>
                </Tr>
            </Tbody>
        </Table>
    );
};

const SystemUserTable = ({ account }) => {
    const { t } = useTranslation();

    return (
        <Table variant="simple">
            <Thead>
                <Tr>
                    <Th isNumeric width="40%"></Th>
                    <Th width="50%"></Th>
                </Tr>
            </Thead>
            <Tbody>
                <Tr>
                    <Td isNumeric>{ t("login.name") }</Td>
                    <Td>{ account.profile.name }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.lastname") }</Td>
                    <Td>{ account.profile.lastname }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.email") }</Td>
                    <Td>{ account.email }</Td>
                </Tr>
                <Tr>
                    <Td isNumeric>{ t("login.mobileNumber") }</Td>
                    <Td>{ account.phoneNumber }</Td>
                </Tr>
            </Tbody>
        </Table>
    );
};

const MyAccount: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const { setAuthToken } = useAuthToken();

    const { data, loading } = useMeQuery();
    const [changePassword] = useChangePasswordMutation();
    const [updateSpecialist] = useUpdateSpecialistMutation();
    const [setAccountProfile] = useSetAccountProfilePictureMutation();
    const [updateLanguage] = useUpdateLanguageMutation();
    const [updateSystemUser] = useUpdateSystemUserMutation();
    const [updateDoctor] = useUpdateDoctorMutation();

    if (loading) {
        return <Loader />;
    }

    const account = data.me;

    const accountType = account.profile?.__typename;

    const handleChangePassword = async (data: IChangePasswordFormValues) => {
        const { data: changePasswordData } = await changePassword({
            variables: {
                oldPassword: data.oldPassword,
                newPassword: data.newPassword
            }
        });

        setAuthToken(changePasswordData.changePassword.token);

        toast({
            description: "Fjalëkalimi u ndryshua.",
            isClosable: true
        });

        onClose();
    };

    const handleUpdateProfileMedicalSpecialist = async (
        data: ISpecialistUpdateFormValues
    ) => {
        if (account.role === AccountRole.MedicalSpecialist) {
            await updateSpecialist({
                variables: {
                    bio: data.bio,
                    birthday: data.birthday,
                    cityId: +data.cityId,
                    countryId: +data.countryId,
                    healthCenterId: +data.healthCenterId,
                    lastname: data.lastname,
                    licenceNumber: data.licenceNumber,
                    name: data.name,
                    phoneNumber: data.phoneNumber,
                    sex: data.sex,
                    specialityId: +data.specialityId
                },
                refetchQueries: [
                    {
                        query: MeDocument
                    }
                ]
            });
        }

        toast({
            description: "Ndryshimet u ruajtën me sukses.",
            isClosable: true
        });

        onClose();
    };

    const handleUpdateProfileDoctor = async (data: IDoctorUpdateFormValues) => {
        if (account.role === AccountRole.Doctor) {
            await updateDoctor({
                variables: {
                    bio: data.bio,
                    birthday: data.birthday,
                    cityId: +data.cityId,
                    countryId: +data.countryId,
                    healthCenterId: +data.healthCenterId,
                    lastname: data.lastname,
                    licenceNumber: data.licenceNumber,
                    name: data.name,
                    phoneNumber: data.phoneNumber,
                    sex: data.sex
                },
                refetchQueries: [
                    {
                        query: MeDocument
                    }
                ]
            });
        }

        toast({
            description: "Ndryshimet u ruajtën me sukses.",
            isClosable: true
        });

        onClose();
    };

    const handleUpdateProfileSystemUser = async (
        data: ISystemUserUpdateFormValues
    ) => {
        if (
            account.role === AccountRole.Author ||
      account.role === AccountRole.Admin
        ) {
            await updateSystemUser({
                variables: {
                    lastname: data.lastname,
                    name: data.name,
                    phoneNumber: data.phoneNumber || ""
                },
                refetchQueries: [
                    {
                        query: MeDocument
                    }
                ]
            });
        }

        toast({
            description: "Ndryshimet u ruajtën me sukses.",
            isClosable: true
        });

        onClose();
    };

    const handleUpdateProfileImage = async (
        data: IAccountProfileImageFormValues
    ) => {
        await setAccountProfile({
            variables: {
                key: data.profileImage.key,
                contentType: "image/jpeg"
            },
            refetchQueries: [
                {
                    query: MeDocument
                }
            ]
        });

        toast({
            description: "Ndryshimet u ruajtën me sukses.",
            isClosable: true
        });

        onClose();
    };

    const handleUpdateProfileLanguage = async (
        data: IAccountProfileLanguageFormValues
    ) => {
        await updateLanguage({
            variables: {
                languageCode: data.languageCode
            },
            refetchQueries: [
                {
                    query: MeDocument
                }
            ]
        });

        toast({
            description: "Ndryshimet u ruajtën me sukses.",
            isClosable: true
        });

        localStorage.setItem("user-language", data.languageCode);
        i18n.changeLanguage(data.languageCode);

        onClose();
    };

    const roleLabel = {
        [AccountRole.Admin]: t("roles.admin"),
        [AccountRole.Author]: t("roles.author"),
        [AccountRole.Doctor]: t("roles.doctor"),
        [AccountRole.MedicalSpecialist]: t("roles.specialist")
    };

    return (
        <>
            <Modal
                closeOnOverlayClick={ false }
                isOpen={ isOpen }
                onClose={ onClose }
                size="xl"
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{ t("account.updateProfile") }</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Tabs isLazy>
                            <TabList>
                                <Tab>{ t("account.profileTab.title") }</Tab>
                                <Tab>{ t("account.passwordTab.title") }</Tab>
                                <Tab>{ t("account.profilePictureTab.title") }</Tab>
                                <Tab>{ t("account.languageTab.title") }</Tab>
                            </TabList>
                            <TabPanels>
                                <TabPanel>
                                    { accountType === AccountType.DoctorProfile &&
                  account.role === AccountRole.Doctor ? (
                      <UpdateDoctorForm
                                                initialValues={ {
                                                    bio: (account.profile as DoctorProfile).bio,
                                                    birthday: (account.profile as DoctorProfile).birthday,
                                                    cityId: `${(account.profile as DoctorProfile).city.id}`,
                                                    countryId: `${
                                                        (account.profile as DoctorProfile).country.id
                                                    }`,
                                                    healthCenterId: `${
                                                        (account.profile as DoctorProfile).healthCenter.id
                                                    }`,
                                                    name: (account.profile as DoctorProfile).name,
                                                    lastname: (account.profile as DoctorProfile).lastname,
                                                    licenceNumber: (account.profile as DoctorProfile)
                                                        .licenceNumber,
                                                    phoneNumber: account.phoneNumber,
                                                    sex: (account.profile as DoctorProfile).sex
                          } }
                                                onSubmit={ (data) => handleUpdateProfileDoctor(data) }
                                            />
                                        ) : accountType === AccountType.DoctorProfile &&
                    account.role === AccountRole.MedicalSpecialist ? (
                        <UpdateSpecialistForm
                                                    initialValues={ {
                                                        bio: (account.profile as DoctorProfile).bio,
                                                        birthday: (account.profile as DoctorProfile).birthday,
                                                        cityId: `${(account.profile as DoctorProfile).city.id}`,
                                                        countryId: `${
                                                            (account.profile as DoctorProfile).country.id
                                                        }`,
                                                        healthCenterId: `${
                                                            (account.profile as DoctorProfile).healthCenter.id
                                                        }`,
                                                        specialityId: `${
                                                            (account.profile as DoctorProfile).speciality.id
                                                        }`,
                                                        name: (account.profile as DoctorProfile).name,
                                                        lastname: (account.profile as DoctorProfile).lastname,
                                                        licenceNumber: (account.profile as DoctorProfile)
                                                            .licenceNumber,
                                                        phoneNumber: account.phoneNumber,
                                                        sex: (account.profile as DoctorProfile).sex
                            } }
                                                    onSubmit={ (data) =>
                                                        handleUpdateProfileMedicalSpecialist(data)
                                                    }
                                                />
                                            ) : accountType === AccountType.SystemUserProfile ? (
                                                <UpdateSystemUserForm
                                                    initialValues={ {
                                                        name: (account.profile as SystemUserProfile).name,
                                                        lastname: (account.profile as SystemUserProfile)
                                                            .lastname,
                                                        phoneNumber: account.phoneNumber
                                                    } }
                                                    onSubmit={ (data) => handleUpdateProfileSystemUser(data) }
                                                />
                                            ) : null }
                                </TabPanel>

                                <TabPanel>
                                    <ChangePasswordForm
                                        onSubmit={ (data) => handleChangePassword(data) }
                                    />
                                </TabPanel>

                                <TabPanel>
                                    <AccountProfileImageForm
                                        onSubmit={ (data) => handleUpdateProfileImage(data) }
                                        initialValues={ {
                                            profileImage: {
                                                url: account.profileImage
                                            }
                                        } }
                                    />
                                </TabPanel>

                                <TabPanel>
                                    <AccountProfileLanguageForm
                                        onSubmit={ (data) => handleUpdateProfileLanguage(data) }
                                        initialValues={ { languageCode: account.language.code } }
                                    ></AccountProfileLanguageForm>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </ModalBody>
                </ModalContent>
            </Modal>
            <>
                <Box
                    borderWidth="1px"
                    borderRadius="lg"
                    overflow="hidden"
                    p={ 2 }
                    background="white"
                >
                    <Title title={ t("account.myAccount") }>
                        <Button
                            colorScheme="blue"
                            variant="outline"
                            size="sm"
                            onClick={ onOpen }
                        >
                            { t("account.updateProfile") }
                        </Button>
                    </Title>
                    <Center>
                        <Stack>
                            <Center>
                                <Avatar
                                    center
                                    size="lg"
                                    name={ `${account.profile.name} ${account.profile.lastname}` }
                                    src={ account.profileImage }
                                />
                            </Center>

                            <Stack>
                                <Text fontSize="xl">
                                    { account.profile.name } { account.profile.lastname }
                                </Text>
                                <Center>
                                    <Text fontSize="sm">{ roleLabel[account.role] }</Text>
                                </Center>
                            </Stack>
                        </Stack>
                    </Center>

                    { accountType === AccountType.DoctorProfile ? (
                        <DoctorTable account={ account } />
                    ) : accountType === AccountType.SystemUserProfile ? (
                        <SystemUserTable account={ account } />
                    ) : null }
                </Box>
            </>
        </>
    );
};

export default MyAccount;
